import React from "react";
import { Path, useForm } from "react-hook-form";
import { RowFormText } from "../Common/Forms/FormText";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import useDisplayMessage from "../Common/useDisplayMessage";
import { BranchDto } from "../../requests/useGetBranches";
import useUpsertBranch, { BranchUpsertDto } from "../../requests/useUpsertBranch";
import { FormEmailList, fromEmailFields, toEmailFields } from "../Common/Forms/FormEmailList";

interface CreateEditBranchProps {
  initialBranch: BranchDto | null;
  onDismiss: () => void;
  onUpsert: (branchName: string) => void;
  mode: ModalMode;
}

type BranchForm = Omit<BranchUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

export const CreateEditBranch = (props: CreateEditBranchProps) => {
  const displayMessage = useDisplayMessage();
  const { id, ...initbranch } = { ...props.initialBranch };
  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<BranchForm>({
    defaultValues: {
      ...initbranch,
      emailAddresses: toEmailFields(initbranch.emailAddresses),
    }
  });

  const upsert = useUpsertBranch(props.initialBranch?.id, {
    onSuccess: () => props.onUpsert(getValues().name),
    onError: (e) => displayMessage.fail(e.message),
  });

  const request = (formValue: BranchForm) => {
    upsert.request({
      ...formValue,
      emailAddresses: fromEmailFields(formValue.emailAddresses),
    });
  }

  const labelSpace = 4;
  const inputSpace = 7;

  const formProps = (name: Path<BranchForm>) => ({
    labelSpace,
    inputSpace,
    name: name,
    setValue,
    register,
    errors,
  });

  return <EntityModal
    mode={props.mode}
    entityName="branch"
    displayMessage={displayMessage}
    onHide={() => props.onDismiss()}
    isSaving={upsert.isLoading && !upsert.isError}
    onSubmit={handleSubmit(request)}
    renderForm={(<>
      <RowFormText {...formProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...formProps('number')}
        displayName="Branch Number"
        options={{
          validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
        }} />
      <RowFormText {...formProps("addressStreet")}
        displayName="Street"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressCity")}
        displayName="City"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressState")}
        displayName="State"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...formProps("addressPostalCode")}
        displayName="Postal Code"
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }} />
      <RowFormText {...formProps("addressCountry")}
        displayName="Country"
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }} />
      <RowFormText {...formProps('phone')}
        options={{
          validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
        }} />
      <FormEmailList {...formProps('emailAddresses')}
        control={control}
        trigger={trigger}
        disabled={props.mode === ModalMode.View}
        max={10} />
    </>)} />
};
